import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { TransactionFilters } from '../components/TransactionFilters';
import { TransactionDetailsPanel } from '../components/TransactionDetailsPanel';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { ApprovalAPI } from '../services/api';
import { PendingSale, UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { useTransactionFilters, toBackendFilters } from '../hooks/useTransactionFilters';
import { hasPermission } from '../utils/rolePermissions';

export const RejectedTransactions: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [allRejectedTransactions, setAllRejectedTransactions] = useState<PendingSale[]>([]);
  const [isDecrypting, setIsDecrypting] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars
  // True total over the full dataset (server aggregate), independent of the
  // fetched page and client-side filtering.
  const [rejectedTotal, setRejectedTotal] = useState(0);

  // Initialize filters - convert PendingSale to Transaction-like structure for filtering
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredTransactions,
    filterStats
  } = useTransactionFilters(allRejectedTransactions.map(transaction => ({
    ...transaction,
    tx_type: 'preauth' as const,
    nmi_response_code: transaction.preauth_response_code,
    nmi_response_text: transaction.preauth_response_text,
    nmi_transactionid: transaction.preauth_transactionid,
    status: transaction.status || 'rejected',
    created_by_name: transaction.created_by_name || transaction.created_by,
    // Include rejection data
    rejected_by: transaction.rejected_by,
    rejected_by_name: transaction.rejected_by_name,
    rejected_at: transaction.rejected_at,
    rejection_reason: transaction.rejection_reason
  })));

  const canDecrypt = hasPermission(user?.role, 'canDecryptCardData');

  const handleViewDetails = (rejectedSale: PendingSale) => {
    // Convert PendingSale to Transaction format for the universal details panel
    const transactionData: UnifiedTransaction = {
      ...rejectedSale,
      tx_type: 'preauth' as const,
      nmi_response_code: rejectedSale.preauth_response_code,
      nmi_response_text: rejectedSale.preauth_response_text,
      nmi_transactionid: rejectedSale.preauth_transactionid,
      status: rejectedSale.status || 'rejected',
      created_by_name: rejectedSale.created_by_name || rejectedSale.created_by,
      // Include rejection data
      rejected_by: rejectedSale.rejected_by,
      rejected_by_name: rejectedSale.rejected_by_name,
      rejected_at: rejectedSale.rejected_at,
      rejection_reason: rejectedSale.rejection_reason
    };
    
    setSelectedTransaction(transactionData);
    setShowDetailsPanel(true);
    // Clear any previous decrypted data
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptData = async (transactionId: string, reason: string) => {
    if (!selectedTransaction) return;
    
    try {
      setIsDecrypting(true);
      // Find the original PendingSale by matching IDs
      const pendingSale = allRejectedTransactions.find(sale => sale.id === selectedTransaction.id);
      if (!pendingSale) {
        throw new Error('Rejected transaction not found');
      }

      const result = await ApprovalAPI.getPendingSaleEncryptedData(pendingSale.id, true, reason);
      setDecryptedData(result);
      setShowEncryptedData(selectedTransaction.id);
      AlertService.success('Card data decrypted successfully');
    } catch (error: any) {
      console.error('Decryption failed:', error);
      AlertService.error(error.message || 'Failed to decrypt card data');
    } finally {
      setIsDecrypting(false);
    }
  };

  const loadRejectedTransactions = async () => {
    try {
      setLoading(true);
      const res = await ApprovalAPI.getRejectedTransactionsPaged({
        offset: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        filters: toBackendFilters(filters),
      });
      setAllRejectedTransactions(res.items);
      setRejectedTotal(res.total);
    } catch (error: any) {
      console.error('Failed to load rejected transactions:', error);
      AlertService.error(error.message || 'Failed to load rejected transactions');
    } finally {
      setLoading(false);
    }
  };

  // Refetch the server page on paging / filter change (filters are debounced upstream).
  useEffect(() => {
    loadRejectedTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Server already returns the current page; render it directly.
  const sortedAndPaginatedTransactions = allRejectedTransactions;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getStatusClass = (status: string) => {
    return 'status-rejected';
  };

  const totalPages = Math.ceil(rejectedTotal / itemsPerPage);

  // Only take over the whole screen on the very first load (no data yet, no filters).
  // On filter-driven refetches the page — and the filter panel — must stay mounted,
  // otherwise every keystroke would unmount the panel, collapse it and steal input
  // focus, so you could only ever enter one character / one filter at a time.
  if (loading && allRejectedTransactions.length === 0 && !hasActiveFilters) {
    return <LoadingScreen isVisible={true} />;
  }

  return (
    <DashboardLayout>
      <div className="rejected-transactions-page">
        <div className="page-header">
          <div className="header-content">
            <h1>🚫 Rejected Transactions</h1>
            <p className="subtitle">
              Transactions that have been rejected by management
            </p>
          </div>
          
          <div className="header-stats">
            <div className="stat-card rejected">
              <span className="stat-label">Total Rejected</span>
              <span className="stat-value">{rejectedTotal.toLocaleString()}</span>
            </div>
            {hasActiveFilters && (
              <div className="stat-card">
                <span className="stat-label">Filtered</span>
                <span className="stat-value">{filterStats.filtered}</span>
              </div>
            )}
          </div>
        </div>

        <TransactionFilters
          filters={filters}
          updateFilter={updateFilter}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          transactions={filteredTransactions}
          filterStats={filterStats}
        />

        <div className="transactions-content">
          {!loading && allRejectedTransactions.length === 0 && !hasActiveFilters ? (
            <div className="empty-state">
              <div className="empty-icon">🎉</div>
              <h3>No Rejected Transactions</h3>
              <p>Great news! There are no rejected transactions to review.</p>
            </div>
          ) : !loading && allRejectedTransactions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No Results Found</h3>
              <p>Try adjusting your filters to see more results.</p>
              <button onClick={clearFilters} className="btn btn-outline-primary">
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="transactions-table">
                  <thead>
                    <tr>
                      {user?.role !== 'agent' && <th>Created By</th>}
                      <th>Rejected By</th>
                      <th>Amount</th>
                      <th>State</th>
                      <th>Category</th>
                      <th>Customer</th>
                      <th>Rejection Reason</th>
                      <th>Rejected At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndPaginatedTransactions.map(transaction => (
                      <tr key={transaction.id}>
                        {user?.role !== 'agent' && (
                          <td className="created-by-cell">
                            {transaction.created_by_name || transaction.created_by || 'Unknown'}
                          </td>
                        )}
                        <td className="rejected-by-cell">
                          {transaction.rejected_by_name || transaction.rejected_by || 'Unknown'}
                        </td>
                        <td className="amount-cell">
                          <span className="amount">
                            ${Number(transaction.amount).toFixed(2)}
                          </span>
                          {transaction.three_ds_status === 'Y' && (
                            <span className="badge-3ds">3DS</span>
                          )}
                          {transaction.three_ds_status === 'Y-3RI' && (
                            <span className="badge-3ds">3RI</span>
                          )}
                        </td>
                        <td>{transaction.sale_state || 'N/A'}</td>
                        <td>{transaction.category || 'N/A'}</td>
                        <td className="customer-cell">
                          <div className="customer-info">
                            <div className="customer-name">
                              {transaction.first_name} {transaction.last_name}
                            </div>
                            {transaction.email && (
                              <div className="customer-email">
                                {transaction.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="rejection-reason-cell">
                          <span className="rejection-reason">
                            {transaction.rejection_reason || 'No reason provided'}
                          </span>
                        </td>
                        <td className="date-cell">
                          {transaction.rejected_at ? new Date(transaction.rejected_at).toLocaleString() : 'N/A'}
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              onClick={() => handleViewDetails(transaction as PendingSale)}
                              className="btn btn-outline-secondary btn-sm"
                              title="View Details"
                            >
                              📋 Details
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalItems={rejectedTotal}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={(newItemsPerPage) => {
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1);
                }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </>
          )}
        </div>

        {/* Payment links never store card data, so the decrypt action is meaningless
            for them (and would hit the pending-sale decrypt endpoint with a link id). */}
        <TransactionDetailsPanel
          transaction={selectedTransaction}
          isOpen={showDetailsPanel}
          onClose={handleCloseDetails}
          decryptedData={decryptedData}
          onDecryptData={canDecrypt && selectedTransaction?.record_type !== 'payment_link' ? handleDecryptData : undefined}
        />
      </div>

      <style>{`
        .rejected-transactions-page {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-content h1 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 2rem;
          font-weight: 600;
        }

        .subtitle {
          color: var(--text-secondary);
          margin: 0;
          font-size: 1rem;
        }

        .header-stats {
          display: flex;
          gap: 1rem;
        }

        .stat-card {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          padding: 1rem 1.5rem;
          text-align: center;
          min-width: 120px;
          box-shadow: 0 1px 3px var(--shadow-light);
        }

        .stat-card.rejected {
          border-left: 4px solid var(--danger);
          background: var(--danger-light);
        }

        .stat-label {
          display: block;
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        .stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .transactions-content {
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px var(--shadow-light);
        }

        .table-container {
          overflow-x: auto;
        }

        .transactions-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .transactions-table th,
        .transactions-table td {
          padding: 0.75rem 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-primary);
          vertical-align: middle;
        }

        .transactions-table th {
          background: var(--bg-secondary);
          font-weight: 600;
          color: var(--text-primary);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .transactions-table tbody tr:hover {
          background: var(--bg-hover);
        }

        .created-by-cell,
        .rejected-by-cell {
          font-weight: 500;
          color: var(--text-primary);
          min-width: 120px;
        }

        .amount-cell .amount {
          font-weight: 600;
          font-family: 'Courier New', monospace;
          color: var(--text-primary);
        }

        .amount-cell {
          min-width: 100px;
        }

        .customer-info {
          min-width: 150px;
        }

        .customer-name {
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 2px;
        }

        .customer-email {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .rejection-reason-cell {
          max-width: 200px;
        }

        .rejection-reason {
          display: block;
          color: var(--danger);
          font-weight: 500;
          line-height: 1.4;
          word-break: break-word;
        }

        .date-cell {
          color: var(--text-secondary);
          font-size: 0.875rem;
          white-space: nowrap;
        }

        .actions-cell {
          text-align: left;
          white-space: nowrap;
          min-width: 100px;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: var(--text-secondary);
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .empty-state p {
          margin-bottom: 1.5rem;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .rejected-transactions-page {
            padding: 1rem;
          }

          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .header-stats {
            justify-content: center;
          }
        }

        @media (max-width: 768px) {
          .header-stats {
            flex-wrap: wrap;
          }

          .stat-card {
            flex: 1;
            min-width: 100px;
          }

          .transactions-table {
            font-size: 0.8rem;
          }

          .transactions-table th,
          .transactions-table td {
            padding: 0.75rem 0.5rem;
          }

          .customer-info {
            min-width: 120px;
          }

          .rejection-reason-cell {
            max-width: 150px;
          }
        }

        .badge-3ds {
          display: inline-block;
          padding: 0.125rem 0.4rem;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 700;
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.3);
          margin-left: 0.25rem;
          vertical-align: middle;
        }
      `}</style>
    </DashboardLayout>
  );
};